/**
 * Application question templates.
 *
 * Every position in the Owner dashboard picks one of these template IDs
 * (`moderation`, `community`, `content`, `partnerships`, `generic`). The
 * template determines which role-specific scenario questions the applicant
 * is asked. All other fields (age, country, timezone, availability,
 * experience, motivation) are shared across all templates.
 *
 * Keys stay stable — they're what gets stored inside application.answers
 * so old submissions keep displaying correctly forever.
 */

export const TEMPLATES = {
  moderation: {
    id: "moderation",
    label: "Moderation / Support (default)",
    description: "For roles that enforce rules, review reports, and interact with users breaking guidelines.",
    questions: [
      {
        id: "denied",
        label: "A server owner is upset because their listing was denied. How would you respond?",
        placeholder: "Explain how you would de-escalate and communicate the decision…",
        minLength: 10,
      },
      {
        id: "rule_violation",
        label: "You discover a server listing that violates our rules. What would you do?",
        placeholder: "Walk us through the exact steps you'd take…",
        minLength: 10,
      },
      {
        id: "staff_abuse",
        label: "Another staff member abuses their permissions. What would you do?",
        placeholder: "Describe how you'd handle it, who you'd report it to, and why…",
        minLength: 10,
      },
    ],
  },
  community: {
    id: "community",
    label: "Community Team",
    description: "For roles focused on growing, promoting, and engaging with the Serverlistr community — not moderation.",
    questions: [
      {
        id: "engagement_idea",
        label: "Pitch one specific event, weekly thread, or activity you'd run to keep members active. Be concrete.",
        placeholder: "e.g. 'Every Friday, a themed server-showcase where…' — the more detail the better.",
        minLength: 10,
      },
      {
        id: "promotion_strategy",
        label: "How would you promote Serverlistr to grow the site's audience? List channels and tactics you'd actually use.",
        placeholder: "e.g. TikTok clips, Reddit threads, cross-promo with server owners, etc.",
        minLength: 10,
      },
      {
        id: "quiet_channel",
        label: "The general chat has been dead for a week. How do you personally revive it without spamming?",
        placeholder: "Give us the exact plan for the next 48 hours…",
        minLength: 10,
      },
      {
        id: "welcoming_newbies",
        label: "How do you make brand-new members feel welcome so they stick around past their first day?",
        placeholder: "Real, human tactics — not just 'say hi'.",
        minLength: 10,
      },
    ],
  },
  content: {
    id: "content",
    label: "Content Creator",
    description: "For applicants making videos, streams, graphics, or written content promoting Serverlistr.",
    questions: [
      {
        id: "portfolio",
        label: "Share links to your best 2-3 pieces of past content (YouTube, TikTok, Twitch, portfolio, etc.).",
        placeholder: "Paste links, one per line. Views/subscriber counts help too.",
        minLength: 10,
      },
      {
        id: "content_ideas",
        label: "Pitch 3 specific pieces of content you'd make for Serverlistr in your first month.",
        placeholder: "Titles, formats, and target audience for each idea.",
        minLength: 10,
      },
      {
        id: "reach",
        label: "What is your current audience size, and how would you use it to funnel viewers to Serverlistr?",
        placeholder: "Real numbers if you have them — subs, average views, engagement.",
        minLength: 10,
      },
    ],
  },
  partnerships: {
    id: "partnerships",
    label: "Partnerships / Outreach",
    description: "For applicants who'll do cold outreach, negotiate cross-promos, and manage relationships with other communities.",
    questions: [
      {
        id: "cold_pitch",
        label: "Write the DM you'd send to the owner of a 50,000-member Discord server asking about a cross-promo.",
        placeholder: "Paste the exact message you'd send — no template blanks.",
        minLength: 10,
      },
      {
        id: "past_deals",
        label: "Describe a partnership, sponsorship, or deal you've closed in the past. What was the outcome?",
        placeholder: "If you haven't closed one yet, describe the closest thing you've done.",
        minLength: 10,
      },
      {
        id: "value_prop",
        label: "In one paragraph, what's the pitch you'd give to a partner about *why* they should promote Serverlistr?",
        placeholder: "This is the exact copy you'd send them.",
        minLength: 10,
      },
    ],
  },
  generic: {
    id: "generic",
    label: "Generic (no scenario questions)",
    description: "Use when a role doesn't need scenario questions. Applicants only fill the shared sections.",
    questions: [],
  },
};

export const TEMPLATE_IDS = Object.keys(TEMPLATES);

export function getTemplate(id) {
  return TEMPLATES[id] || TEMPLATES.moderation;
}
